import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import BankTable from '../../../components/tables/BankTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useBankApi } from '../../../hooks/useBankApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { BankDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const BankList: React.FC = () => {
  const { search, remove, isLoading, error } = useBankApi();
  const [items, setItems] = useState<BankDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Banks', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'Name',
        sortDescending: false,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Banks:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: BankDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting Bank:', error);
    }
  };

  return (
    <AdminLayout
      title="Banks"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Administrator']}
    >
    <Card
        title="Manage Banks"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/banks/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                    <FormField
                            id="name"
                            label="Search by Name"
                            value={searchFilters.name || ''}
                            onChange={(e) => setSearchFilters({ ...searchFilters, name: e.target.value })}
                            placeholder="Search by Name"
                        />
                        <FormField
                            id="branchCode"
                            label="Search by Branch Code"
                            value={searchFilters.branchCode || ''}
                            onChange={(e) => setSearchFilters({ ...searchFilters, branchCode: e.target.value })}
                            placeholder="Search by Branch Code"
                        />

          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <BankTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/banks/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default BankList;